import { ArtifactPanel } from '@/components/artifact/ArtifactPanel';
import {
  StreamingArtifactPanel,
  type StreamingArtifactState,
} from '@/components/artifact/StreamingArtifactPanel';
import { extractStreamingStringField } from '@/components/artifact/utils';
import { ActiveSkillChips } from '@/components/chat/ActiveSkillChips';
import { isPendingStatus } from '@/components/chat/AttachmentChips';
import { COMPOSER_MAX_ATTACHMENTS, ChatComposer } from '@/components/chat/ChatComposer';
import { ChatFooterNote } from '@/components/chat/ChatFooterNote';
import { FileDropOverlay } from '@/components/chat/FileDropOverlay';
import { HilModal } from '@/components/chat/HilModal';
import { InlineRenameInput } from '@/components/chat/InlineRenameInput';
import { MessageList } from '@/components/chat/MessageList';
import type { StreamSegment } from '@/components/chat/ToolCallChip';
import { ArtifactProfilePanel } from '@/components/dashboard/ArtifactProfilePanel';
import { DashboardSaveDialog } from '@/components/dashboard/DashboardSaveDialog';
import { SidebarOpenerButton } from '@/components/layout/SidebarOpenerButton';
import { WikiPeekPanel } from '@/components/wiki/WikiPeekPanel';
import { useFileDropUpload } from '@/hooks/useFileDropUpload';
import { useSessionArtifacts } from '@/hooks/useSessionArtifacts';
import { ApiError, api } from '@/lib/api';
import { useAppSettings } from '@/lib/appSettings';
import type { ComposerAttachment } from '@/lib/attachment';
import { useChatModel } from '@/lib/chatModel';
import {
  type AwaitUserInputEvent,
  type SendChatMessageOptions,
  followChatStream,
  resolveInterruptStream,
  sendChatMessage,
} from '@/lib/chatStream';
import { takePendingMessage } from '@/lib/pendingChat';
import { useIsMobile } from '@/lib/useIsMobile';
import { useSidebarCollapsed } from '@/lib/useSidebarCollapsed';
import { chatSessionRoute } from '@/router';
import {
  ArtifactKindSchema,
  type ChatMessage,
  type ChatSessionDetail,
  type FileUpload,
} from '@innermate/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Archive, Loader2, Star, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

interface Props {
  sessionId: string;
}

const PANEL_WIDTH_STORAGE_KEY = 'innermate.artifactPanel.width';
const PANEL_WIDTH_DEFAULT = 480;
const PANEL_WIDTH_MIN = 360;
/** 채팅 영역에 최소 420px 는 남겨둔다. 실제 max 는 drag 시 viewport 기준으로 다시 clamp. */
const PANEL_WIDTH_MAX_FALLBACK = 1200;

function readStoredPanelWidth(): number {
  if (typeof window === 'undefined') return PANEL_WIDTH_DEFAULT;
  const raw = window.localStorage.getItem(PANEL_WIDTH_STORAGE_KEY);
  if (!raw) return PANEL_WIDTH_DEFAULT;
  const n = Number.parseInt(raw, 10);
  if (!Number.isFinite(n)) return PANEL_WIDTH_DEFAULT;
  return Math.max(PANEL_WIDTH_MIN, Math.min(PANEL_WIDTH_MAX_FALLBACK, n));
}

export function ChatSession({ sessionId }: Props) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [isStreaming, setIsStreaming] = useState(false);
  // SSE 이벤트 도착 순서대로 쌓이는 세그먼트. text segment 는 delta 누적, toolcall segment 는 pending→ok/error.
  const [streamSegments, setStreamSegments] = useState<StreamSegment[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [attachments, setAttachments] = useState<ComposerAttachment[]>([]);
  const [pendingUser, setPendingUser] = useState<{
    content: string;
    fileIds: string[];
  } | null>(null);
  const [streamingArtifact, setStreamingArtifact] = useState<StreamingArtifactState | null>(null);
  const [contextUsage, setContextUsage] = useState<{
    estimatedTokens: number;
    contextWindow: number;
    usageRatio: number;
  } | null>(null);
  /** LLM 답변에 [[slug]] 위키링크 클릭 시 우측 패널에 미리보기. artifact 와 슬롯 공유 — peek 활성 시 artifact 잠시 가려짐. */
  const [peekedWikiSlug, setPeekedWikiSlug] = useState<string | null>(null);

  // Human-in-the-loop: 서버가 사용자 응답을 기다리는 동안 떠있는 모달 상태.
  const [pendingHil, setPendingHil] = useState<AwaitUserInputEvent | null>(null);
  const [hilSubmitting, setHilSubmitting] = useState(false);
  // 다른 채팅 세션으로 이동하면 이전 세션의 우측 패널/오버레이 state 를 자동 정리.
  // ChatSession 컴포넌트는 라우터에 의해 재사용되어 sessionId prop 만 바뀌므로 state 가 잔존함
  // (정리 안 하면 이전 세션의 아티팩트/스트리밍 잔상이 그대로 남는다). 아티팩트 "선택 해제" 는
  // 공유링크(?artifact=) 와의 충돌을 피하려 별도 effect 에서 처리.
  useEffect(() => {
    setPeekedWikiSlug(null);
    setStreamingArtifact(null);
    setIsFullscreen(false);
    setAnalyzeArtifactState({ open: false });
    setSaveDashboardState({ open: false });
    // 다른 세션으로 전환하면 이전 세션의 스킬 선택 picker(미해결 interrupt 모달)도 닫는다.
    // 직후 sessionId effect 가 새 세션의 active interrupt 만 복구 로드한다.
    setPendingHil(null);
  }, [sessionId]);
  const [panelWidth, setPanelWidth] = useState<number>(readStoredPanelWidth);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const isMobile = useIsMobile();
  const { open: openSidebar } = useSidebarCollapsed();
  /** dashboard 저장 다이얼로그 상태. ArtifactPanel 의 [저장] 버튼이 트리거. */
  const [saveDashboardState, setSaveDashboardState] = useState<
    { open: false } | { open: true; artifactId: string; defaultTitle: string }
  >({ open: false });
  /** chat dashboard 분석 패널 상태. ArtifactPanel 의 [Activity] 버튼이 트리거. */
  const [analyzeArtifactState, setAnalyzeArtifactState] = useState<
    { open: false } | { open: true; artifactId: string; content: string }
  >({ open: false });
  // 모바일에선 사이드/풀스크린 구분이 무의미 — 항상 풀스크린으로 띄운다.
  const renderFullscreen = isFullscreen || isMobile;
  const [shareFeedback, setShareFeedback] = useState<'idle' | 'copied' | 'error'>('idle');
  const [dropError, setDropError] = useState<string | null>(null);
  // 응답 시간 표시용. streamStartedAt 이 set 되면 100ms 간격으로 liveElapsedMs 갱신.
  // null 이면 표시 안 함 (follow 진입 시처럼 정확한 시작점을 모를 때).
  const [streamStartedAt, setStreamStartedAt] = useState<number | null>(null);
  const [liveElapsedMs, setLiveElapsedMs] = useState<number>(0);
  const [renamingTitle, setRenamingTitle] = useState(false);
  const sentInitialRef = useRef<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const shareFeedbackTimerRef = useRef<number | null>(null);

  const appSettings = useAppSettings();
  const showResponseTime = appSettings.data?.showResponseTime ?? false;

  // streamStartedAt 이 살아있으면 100ms 마다 wall-clock elapsed 를 갱신.
  useEffect(() => {
    if (streamStartedAt === null) {
      setLiveElapsedMs(0);
      return;
    }
    setLiveElapsedMs(Date.now() - streamStartedAt);
    const t = window.setInterval(() => {
      setLiveElapsedMs(Date.now() - streamStartedAt);
    }, 100);
    return () => window.clearInterval(t);
  }, [streamStartedAt]);

  const sessionQuery = useQuery({
    queryKey: ['chat', 'session', sessionId],
    queryFn: () => api.chatGetSession(sessionId),
    // 세션은 사용자 액션 (메시지 전송/모델 변경/삭제) 으로만 변하므로
    // stale refetch / focus refetch 는 불필요. 명시적 invalidate 만 신뢰.
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
    retry: (failureCount, err) => {
      if (err instanceof ApiError && err.status === 404) return false;
      return failureCount < 2;
    },
  });

  const sessionArtifacts = useSessionArtifacts(sessionId);

  const session = sessionQuery.data;
  const model = useChatModel({
    session: session
      ? {
          id: session.id,
          llmEndpointId: session.llmEndpointId,
          llmBondingId: session.llmBondingId,
          extendedThinking: session.extendedThinking,
        }
      : undefined,
  });

  // 사용자 메시지 bubble / composer 첨부 칩의 status lookup 을 위해 파일 목록을 캐시해둠.
  // AttachFilesDialog 가 같은 queryKey 를 쓰므로 다이얼로그 열렸을 때 이미 채워짐.
  // 인제스트가 아직 끝나지 않은 (비종료 status) 파일이 하나라도 있으면 3초마다 폴링해 워커가
  // ready/failed 로 바꾼 status 를 받아온다. 모두 종료되면 폴링 자동 정지 (무한 폴링 방지).
  const sessionFilesQuery = useQuery({
    queryKey: ['files', sessionId],
    queryFn: () => api.fileList(sessionId),
    staleTime: 30_000,
    refetchInterval: (q) =>
      (q.state.data?.files ?? []).some((f) => isPendingStatus(f.status)) ? 3000 : false,
  });
  // 첨부 파일 lookup 은 sticky 누적 맵으로 관리한다. fileList(sessionId) 는 "메시지에 attach 된"
  // 파일만 반환하므로, 방금 업로드했지만 아직 전송 안 된(턴 진행 중) 파일은 빠진다. 쿼리 결과만
  // source 로 쓰면 3초 refetch 가 그 공백을 그대로 반영해 user 버블이 "(삭제됨)" 으로 잘못 표시된다.
  // → 한 번 알게 된 파일(쿼리 결과 + 우리가 업로드한 객체)을 누적 보관해 refetch 가 지우지 못하게 한다.
  const [knownFiles, setKnownFiles] = useState<Map<string, FileUpload>>(new Map());
  const mergeKnownFiles = useCallback((files: FileUpload[]) => {
    if (files.length === 0) return;
    setKnownFiles((prev) => {
      let changed = false;
      const m = new Map(prev);
      for (const f of files) {
        if (m.get(f.id) !== f) {
          m.set(f.id, f);
          changed = true;
        }
      }
      return changed ? m : prev;
    });
  }, []);
  // 세션 전환 시 누적 맵 리셋 (세션 간 누수/메모리 누적 방지). merge effect 보다 먼저 선언해
  // sessionId 변경 시 리셋이 먼저 실행되도록 한다.
  // biome-ignore lint/correctness/useExhaustiveDependencies: sessionId 변경 시에만 리셋
  useEffect(() => {
    setKnownFiles(new Map());
  }, [sessionId]);
  // 쿼리가 서버 최신 status 를 가져오면 누적 맵에 merge (덮어쓰기 → status 전이 반영).
  useEffect(() => {
    mergeKnownFiles(sessionFilesQuery.data?.files ?? []);
  }, [sessionFilesQuery.data, mergeKnownFiles]);
  const filesById = knownFiles;

  // 답변에 등장하는 [[slug]] 위키링크 chip 을 슬러그 대신 페이지 제목으로 표시하기 위해
  // 위키 인덱스 (모든 페이지 slug + title) 를 캐시. 5분 stale.
  const wikiIndexQuery = useQuery({
    queryKey: ['wiki', 'index'],
    queryFn: api.wikiIndex,
    staleTime: 5 * 60_000,
  });
  const wikiSlugToTitle = useMemo(() => {
    const map = new Map<string, string>();
    const idx = wikiIndexQuery.data;
    if (!idx) return map;
    for (const cat of idx.categories) {
      for (const p of cat.pages) map.set(p.slug, p.title);
    }
    for (const p of idx.uncategorized) map.set(p.slug, p.title);
    return map;
  }, [wikiIndexQuery.data]);

  // SSE 이벤트별 setState 핸들러 묶음. stream(POST) / tryFollow(GET) 둘 다 같은 핸들러를 쓴다.
  // setState 들은 stable 이라 의존성 배열에 sessionArtifacts.handleArtifactChange 만 두면 충분하다.
  const streamHandlers = useCallback<() => SendChatMessageOptions>(
    () => ({
      onDelta: (delta) => {
        // 마지막 segment 가 text 면 concat, 아니면 (비어있거나 toolcall 다음) 새 text segment push.
        setStreamSegments((prev) => {
          // 진행 상황(status) 안내는 일시적 — 실제 답변(delta)이 오면 소거한다.
          // (재생성 안내 "…다시 생성하고 있습니다…" 도 새 답변이 흐르면 이 시점에 사라진다.)
          let base = prev;
          while (base.length > 0 && base[base.length - 1]?.type === 'status') {
            base = base.slice(0, -1);
          }
          const last = base[base.length - 1];
          if (last && last.type === 'text') {
            const updated: StreamSegment = { type: 'text', value: last.value + delta };
            return [...base.slice(0, -1), updated];
          }
          return [...base, { type: 'text', value: delta }];
        });
      },
      onStatus: (msg) => {
        setStreamSegments((prev) => {
          const last = prev[prev.length - 1];
          if (last && last.type === 'status') {
            return [...prev.slice(0, -1), { type: 'status', message: msg }];
          }
          return [...prev, { type: 'status', message: msg }];
        });
      },
      onReset: () => {
        // 비정상 출력(무한 반복/끝없는 생성) 감지 → 재생성. 지금까지 누적한 진행 중 출력과
        // 아티팩트 미리보기를 즉시 비운다. isStreaming 은 유지 — 곧 새 delta 가 흐른다.
        setStreamSegments([]);
        setStreamingArtifact(null);
      },
      onToolCallStart: (ev) => {
        setStreamSegments((prev) => {
          // 진행 상황(status) 안내는 일시적 — 도구 호출이 시작되면(텍스트 없이 툴만 부르는
          // 턴 포함) 소거한다. (onDelta 와 동일 처리.)
          let base = prev;
          while (base.length > 0 && base[base.length - 1]?.type === 'status') {
            base = base.slice(0, -1);
          }
          return [
            ...base,
            {
              type: 'toolcall',
              call: {
                id: ev.toolCallId,
                toolKey: ev.toolKey,
                status: 'pending',
                summary: null,
              },
            },
          ];
        });
        // dashboard.create 시 사이드바 자동 collapse 는 사용자 요청으로 비활성화.
        if (ev.toolKey === 'system.create_artifact' || ev.toolKey === 'system.update_artifact') {
          setStreamingArtifact({
            toolCallId: ev.toolCallId,
            mode: ev.toolKey === 'system.create_artifact' ? 'create' : 'update',
            partialJson: '',
          });
          // 새 artifact 가 우측 슬롯을 점유하므로 wiki peek 자동 정리.
          setPeekedWikiSlug(null);
        }
      },
      onToolCallInputDelta: (toolCallId, deltaJson) => {
        setStreamingArtifact((cur) => {
          if (!cur || cur.toolCallId !== toolCallId) return cur;
          const nextJson = cur.partialJson + deltaJson;
          const kindRaw = extractStreamingStringField(nextJson, 'kind');
          const kindParsed = kindRaw ? ArtifactKindSchema.safeParse(kindRaw) : null;
          return {
            ...cur,
            partialJson: nextJson,
            kind: kindParsed?.success ? kindParsed.data : cur.kind,
            title: extractStreamingStringField(nextJson, 'title') ?? cur.title,
            content: extractStreamingStringField(nextJson, 'content') ?? cur.content,
          };
        });
      },
      onToolCallOutput: (ev) => {
        setStreamSegments((prev) =>
          prev.map((seg) =>
            seg.type === 'toolcall' && seg.call.id === ev.toolCallId
              ? {
                  type: 'toolcall',
                  call: {
                    ...seg.call,
                    status: ev.status,
                    summary: ev.summary,
                    output: ev.status === 'ok' ? ev.details : undefined,
                    error: ev.status === 'error' ? ev.summary : null,
                  },
                }
              : seg,
          ),
        );
        setStreamingArtifact((cur) => {
          if (!cur || cur.toolCallId !== ev.toolCallId) return cur;
          // 오류여도 패널을 닫지 않는다 — LLM 이 create_artifact 와 email.send 를 parallel 로
          // 호출할 때 artifact input JSON 이 불완전해 서버 검증 실패가 발생하는 케이스 대응.
          // isDone=true 로 마킹하면 사용자가 스트리밍된 내용을 그대로 볼 수 있다.
          if (ev.status === 'error') return { ...cur, isDone: true };
          return cur;
        });
      },
      onArtifactCreated: (ev) => {
        // null 로 즉시 클리어하지 않는다 — artifact 목록 refetch 완료 전까지는
        // streamingArtifact 가 non-null 이어야 showArtifactPanel = true 를 유지.
        // isDone=true 로 마킹하면 렌더에서 ArtifactPanel 이 우선 표시되고,
        // selected 가 non-null 이 되는 시점에 useEffect 가 정리한다.
        setStreamingArtifact((cur) => (cur ? { ...cur, isDone: true } : null));
        sessionArtifacts.handleArtifactChange(ev.artifactId);
      },
      onArtifactUpdated: (ev) => {
        setStreamingArtifact(null);
        sessionArtifacts.handleArtifactChange(ev.artifactId);
      },
      onWarning: (message) => setError(message),
      onDone: (responseTimeMs) => {
        console.log('[chat] done received', { responseTimeMs });
        // 안전망 — root cause 와 무관하게 done event 받자마자 화면 회복.
        // mutation 의 finally 도달이 어떤 이유로 막혀도 placeholder bubble 사라지고
        // sessionQuery refetch 로 마지막 assistant 메시지가 messages 영역에 즉시 표시.
        setIsStreaming(false);
        setStreamSegments([]);
        setPendingUser(null);
        setStreamingArtifact(null);
        setStreamStartedAt(null);
        void qc.invalidateQueries({ queryKey: ['chat', 'session', sessionId] });
        // 답변에 새로 생성된 위키 페이지 슬러그가 등장할 수 있어 슬러그→제목 매핑 즉시 갱신.
        void qc.invalidateQueries({ queryKey: ['wiki', 'index'] });
      },
      onAwaitUserInput: (ev) => {
        console.log('[chat] await_user_input received', {
          interruptId: ev.interruptId,
          kind: ev.prompt.kind,
          title: ev.prompt.title,
        });
        setPendingHil(ev);
        setHilSubmitting(false);
        // HIL 대기 중에는 스트리밍 상태를 해제해 "생각 중…" 버블을 없앤다.
        // 이 시점에 서버가 이미 assistant 메시지를 DB 에 INSERT 했으므로
        // invalidate refetch 가 완료되면 LLM 텍스트가 정상 표시된다.
        // (resolveHil 이 사용자 응답 시 isStreaming 을 다시 true 로 올린다.)
        setIsStreaming(false);
        setStreamStartedAt(null);
        // streamSegments 의 partial delta 가 남아있으면 DB row 와 이중 표시되므로 정리.
        setStreamSegments([]);
        void qc.invalidateQueries({ queryKey: ['chat', 'session', sessionId] });
      },
      onInterruptResolved: (id) => {
        console.log('[chat] interrupt_resolved received', { interruptId: id });
        setPendingHil((cur) => (cur && cur.interruptId === id ? null : cur));
      },
      onContextUsage: (info) => setContextUsage(info),
    }),
    [sessionArtifacts.handleArtifactChange, qc, sessionId],
  );

  // 새로고침 직후 채팅 화면이 로드되면 활성 인터럽트가 있는지 확인 — 있으면 모달 복구.
  useEffect(() => {
    let canceled = false;
    (async () => {
      try {
        const active = await api.chatGetActiveInterrupt(sessionId);
        if (!canceled && active) {
          setPendingHil({ interruptId: active.interruptId, prompt: active.prompt });
        }
      } catch {
        // 401/network 등은 조용히 무시 — 사용자가 다시 메시지 보내면 새 인터럽트가 생긴다.
      }
    })();
    return () => {
      canceled = true;
    };
  }, [sessionId]);

  const resolveHil = useCallback(
    async (body: import('@innermate/shared').InterruptResolveRequest) => {
      if (!pendingHil) return;
      const interruptId = pendingHil.interruptId;
      // 모달 닫고 사용자 선택 라벨을 낙관적으로 user bubble 로 즉시 표시.
      // 첫 POST /messages 의 SSE 가 라이브 events 를 받고 있으므로 abort/reset 안 함.
      // sessionQuery invalidate 도 호출 안 함 — LLM 이 빠르게 진행해 assistant tool_call 이
      // 영속화된 시점에 refetch 하면 streamSegments 와 messages 에 같은 chip 이 동시에 보여서
      // 두 번 표시되는 현상이 발생한다. 첫 stream 의 done 시점 finally 가 invalidate 를 처리.
      let optimisticContent: string | null = null;
      if (body.kind === 'choice') {
        const choice = pendingHil.prompt.choices.find((c) => c.id === body.choiceId);
        optimisticContent = choice?.label ?? null;
      } else if (body.kind === 'free') {
        optimisticContent = body.text;
      }
      setPendingHil(null);
      setHilSubmitting(true);
      setError(null);
      // streaming UI 무조건 활성화 — race / 가드 어디서 깨져도 picker 클릭 시 다시 켜진다.
      // - chooser 케이스 (첫 SSE 가 reply.end 됨): streamSegments 비어있음. set 해도 동일.
      // - fast-path 케이스 (첫 SSE 살아있음): segments 가 잠깐 wipe → onDelta 가 곧 다시 push.
      //   잠깐 깜빡임 < 진행 표시 누락 보다 낫다.
      setIsStreaming(true);
      setStreamSegments([]);
      setStreamStartedAt(Date.now());
      if (optimisticContent !== null) {
        setPendingUser({ content: optimisticContent, fileIds: [] });
      }
      // Fallback polling — fast-path 시 첫 SSE-A 가 LLM iteration 의 emit (delta/tool_call/done)
      // 을 정상 전달해야 하지만, 실제 환경에서 await_user_input 후 emit 들이 클라에 도달 안 하는
      // 케이스 발견 (subscribers: 1 인데 reader 가 깨진 듯). 그래서 resolve 후 일정 시간마다
      // sessionQuery 강제 refetch — DB persist 된 assistant 메시지를 가져와 화면 회복.
      //
      // 부작용 회피: polling 으로 messages refetch 시 DB 에 user-choice + assistant 둘 다 있으면
      // 그 시점에 streamSegments / pendingUser 를 정리해 메시지 순서 꼬임 방지.
      const startedPollingAt = Date.now();
      let polls = 0;
      const POLL_INTERVAL_MS = 3500;
      const POLL_MAX_MS = 60000;
      const pollId = window.setInterval(async () => {
        polls += 1;
        if (Date.now() - startedPollingAt > POLL_MAX_MS) {
          window.clearInterval(pollId);
          return;
        }
        await qc.invalidateQueries({ queryKey: ['chat', 'session', sessionId] });
        // refetch 직후 messages 의 마지막이 assistant 라면 LLM 응답이 DB 에 도착한 것 →
        // optimistic state (streamSegments / pendingUser) 정리해 화면 정합 보장.
        const data = qc.getQueryData<{ messages: Array<{ role: string; content: string }> }>([
          'chat',
          'session',
          sessionId,
        ]);
        const last = data?.messages?.[data.messages.length - 1];
        if (last?.role === 'assistant') {
          setStreamSegments([]);
          setPendingUser(null);
          setIsStreaming(false);
          window.clearInterval(pollId);
          return;
        }
        if (polls > 0 && !isStreaming) {
          window.clearInterval(pollId);
        }
      }, POLL_INTERVAL_MS);

      try {
        await resolveInterruptStream(sessionId, interruptId, body, streamHandlers());
      } catch (e) {
        if (e instanceof DOMException && e.name === 'AbortError') return;
        if (e instanceof Error && e.name === 'AbortError') return;
        setError(e instanceof Error ? e.message : '응답 실패');
      } finally {
        setHilSubmitting(false);
        // 폴링은 onDone 또는 max timeout 으로 stop. resolveInterruptStream 의 finally 도달
        // 시점엔 종종 LLM 응답이 아직 안 됐을 수 있어 여기서 clear 하지 않는다.
      }
    },
    [pendingHil, sessionId, streamHandlers, qc, isStreaming],
  );

  const closeHil = useCallback(() => {
    void resolveHil({ kind: 'skip' });
  }, [resolveHil]);

  const streamMut = useMutation({
    mutationFn: async (params: {
      content: string;
      fileIds: string[];
      endpointId?: string;
      bondingId?: string;
      extendedThinking?: boolean;
      replaceFromMessageId?: string;
      signal?: AbortSignal;
      /** chooser 모달 (await_user_input) 받았을 때 호출 — stream() 의 reset 가드용. */
      onAwaitUserInputObserver?: () => void;
    }) => {
      const baseHandlers = streamHandlers();
      const wrappedHandlers = {
        ...baseHandlers,
        onAwaitUserInput: (ev: AwaitUserInputEvent) => {
          params.onAwaitUserInputObserver?.();
          baseHandlers.onAwaitUserInput?.(ev);
        },
      };
      return sendChatMessage(sessionId, params.content, {
        ...wrappedHandlers,
        fileIds: params.fileIds,
        endpointId: params.endpointId,
        bondingId: params.bondingId,
        extendedThinking: params.extendedThinking,
        replaceFromMessageId: params.replaceFromMessageId,
        signal: params.signal,
      });
    },
  });

  const stream = async (
    content: string,
    fileIds: string[],
    overrides?: {
      endpointId?: string;
      bondingId?: string;
      extendedThinking?: boolean;
      /** Index → Session 으로 첫 메시지 넘길 때, send 직후 시각을 그대로 사용. */
      startedAt?: number;
      /** 마지막 user 메시지 편집·재전송 시, 교체 대상 user 메시지 id. */
      replaceFromMessageId?: string;
    },
  ) => {
    // 이전 stream 이 아직 살아있으면 먼저 끊고 시작 (StrictMode 이중 실행 / 빠른 재전송 대비).
    abortRef.current?.abort();
    const ctl = new AbortController();
    abortRef.current = ctl;

    setError(null);
    setIsStreaming(true);
    setStreamSegments([]);
    setPendingUser({ content, fileIds });
    setStreamStartedAt(overrides?.startedAt ?? Date.now());

    // chooser 분기 (await_user_input 받고 server reply.end) 인 경우 mutation 종료 시에도
    // streaming UI state 를 reset 하지 않는다. picker → /resolve 의 새 SSE 가 같은 isStreaming/
    // streamSegments 자리에 token 을 이어 그릴 수 있도록. /resolve SSE 의 onDone 이 최종 reset
    // + invalidate 를 담당.
    let suspendedForUserInput = false;

    try {
      const sel = model.selected;
      const endpointId = overrides?.endpointId ?? (sel?.kind === 'endpoint' ? sel.id : undefined);
      const bondingId = overrides?.bondingId ?? (sel?.kind === 'bonding' ? sel.id : undefined);
      const thinkingFromModel =
        sel?.supportsExtendedThinking === true ? model.extendedThinking : undefined;
      const extendedThinking =
        overrides?.extendedThinking !== undefined ? overrides.extendedThinking : thinkingFromModel;
      await streamMut.mutateAsync({
        content,
        fileIds,
        endpointId,
        bondingId,
        extendedThinking,
        replaceFromMessageId: overrides?.replaceFromMessageId,
        signal: ctl.signal,
        onAwaitUserInputObserver: () => {
          suspendedForUserInput = true;
        },
      });
      if (!suspendedForUserInput) {
        await Promise.all([
          qc.invalidateQueries({ queryKey: ['chat', 'session', sessionId] }),
          qc.invalidateQueries({ queryKey: ['chat', 'sessions'] }),
          qc.invalidateQueries({ queryKey: ['files'] }),
          // classifier 가 turn 마다 skillIds 를 갱신할 수 있으므로 chip 도 새로고침.
          qc.invalidateQueries({ queryKey: ['chat-active-skills', sessionId] }),
        ]);
      }
    } catch (e) {
      if (e instanceof DOMException && e.name === 'AbortError') return;
      if (e instanceof Error && e.name === 'AbortError') return;
      setError(e instanceof Error ? e.message : '응답 실패');
    } finally {
      if (abortRef.current === ctl) abortRef.current = null;
      if (!suspendedForUserInput) {
        setIsStreaming(false);
        setStreamSegments([]);
        setPendingUser(null);
        setStreamingArtifact(null);
        setStreamStartedAt(null);
      }
    }
  };

  // 새로고침/창닫기 후 재진입 시 진행 중 응답에 합류. 진행 중 stream 이 없으면 즉시 종료 (UI 평소).
  // 콜백은 stream() 과 같은 핸들러 묶음 — 같은 segments 가 재구성된다.
  const tryFollow = async () => {
    if (abortRef.current) return;
    const ctl = new AbortController();
    abortRef.current = ctl;
    setError(null);
    // run 이 있는지 모르는 상태이지만 일단 streaming UI 켜둔다 — null 이면 finally 가 즉시 끄고
    // 평소 화면으로 복귀. 200 OK 면 서버 events replay 가 도착하기 전 잠깐 spinner 보임.
    setIsStreaming(true);
    setStreamSegments([]);
    try {
      const result = await followChatStream(sessionId, {
        ...streamHandlers(),
        signal: ctl.signal,
      });
      if (result === null) return; // 진행 중 stream 없음
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['chat', 'session', sessionId] }),
        qc.invalidateQueries({ queryKey: ['chat', 'sessions'] }),
        qc.invalidateQueries({ queryKey: ['files'] }),
        qc.invalidateQueries({ queryKey: ['chat-active-skills', sessionId] }),
      ]);
    } catch (e) {
      if (e instanceof DOMException && e.name === 'AbortError') return;
      if (e instanceof Error && e.name === 'AbortError') return;
      setError(e instanceof Error ? e.message : '응답 재합류 실패');
    } finally {
      if (abortRef.current === ctl) abortRef.current = null;
      setIsStreaming(false);
      setStreamSegments([]);
      setPendingUser(null);
      setStreamingArtifact(null);
    }
  };

  // 사용자가 메시지를 전송한 시점에 pending 첨부들을 업로드한 뒤 stream 을 시작한다.
  // 한 개라도 업로드에 실패하면 해당 칩만 'failed' 로 attachments 에 남기고 나머지는 그대로 진행.
  const onSend = async (content: string) => {
    setIsStreaming(true);
    setError(null);
    const snapshot = attachments;
    const pendings = snapshot.filter(
      (a): a is Extract<ComposerAttachment, { kind: 'pending' }> => a.kind === 'pending',
    );
    const uploadedFileObjs = snapshot
      .filter((a): a is Extract<ComposerAttachment, { kind: 'uploaded' }> => a.kind === 'uploaded')
      .map((a) => a.file);
    const alreadyUploadedIds = uploadedFileObjs.map((f) => f.id);

    if (pendings.length === 0) {
      setAttachments([]);
      // 이미 업로드된(+다이얼로그 등) 첨부도 누적 맵에 넣어야 턴 중 "(삭제됨)" 이 안 뜬다.
      mergeKnownFiles(uploadedFileObjs);
      void stream(content, alreadyUploadedIds);
      return;
    }

    setAttachments((cur) =>
      cur.map((a) =>
        a.kind === 'pending' ? { ...a, phase: 'uploading' as const, error: undefined } : a,
      ),
    );

    const results = await Promise.all(
      pendings.map(async (p) => {
        try {
          const uploaded = await api.fileUpload(p.file);
          return { ok: true as const, localId: p.localId, uploaded };
        } catch (err) {
          return {
            ok: false as const,
            localId: p.localId,
            error: err instanceof Error ? err.message : '업로드 실패',
          };
        }
      }),
    );

    const successById = new Map<string, FileUpload>();
    const failedById = new Map<string, string>();
    for (const r of results) {
      if (r.ok) successById.set(r.localId, r.uploaded);
      else failedById.set(r.localId, r.error);
    }

    // 새로 업로드한 파일 + 이미 업로드돼 있던 첨부를 모두 누적 맵에 넣어 턴 중 "(삭제됨)" 방지.
    mergeKnownFiles([...uploadedFileObjs, ...Array.from(successById.values())]);
    if (successById.size > 0) {
      // 새 업로드는 background refetch 로 서버 최신 status 반영 (effect 가 knownFiles 에 merge).
      qc.invalidateQueries({ queryKey: ['files'] });
    }

    // 성공한 pending 과 기존 uploaded 는 메시지에 포함되어 비워지고, 실패한 pending 만 칩에 남김.
    setAttachments(() => {
      const remaining: ComposerAttachment[] = [];
      for (const a of snapshot) {
        if (a.kind === 'uploaded') continue;
        if (successById.has(a.localId)) continue;
        const errMsg = failedById.get(a.localId);
        if (errMsg !== undefined) {
          remaining.push({ ...a, phase: 'failed', error: errMsg });
        }
      }
      return remaining;
    });

    if (failedById.size > 0) {
      setError(`첨부 ${failedById.size}개 업로드 실패. 칩에서 다시 시도해 주세요.`);
    }

    const newUploadedIds = Array.from(successById.values()).map((f) => f.id);
    const fileIds = [...alreadyUploadedIds, ...newUploadedIds];
    void stream(content, fileIds);
  };

  // 윈도우 전역 드래그앤드롭 — 드롭 시점엔 업로드하지 않고 pending 첨부로만 추가한다.
  // 실제 업로드는 사용자가 메시지를 보낼 때 onSend 가 일괄 처리.
  const { isDragging } = useFileDropUpload({
    maxAttachments: COMPOSER_MAX_ATTACHMENTS,
    currentCount: attachments.length,
    allowedExts: appSettings.data?.uploadAllowedExts,
    onFilesDropped: (files) => {
      setDropError(null);
      setAttachments((prev) => {
        const slots = COMPOSER_MAX_ATTACHMENTS - prev.length;
        const take = files.slice(0, Math.max(0, slots));
        const additions: ComposerAttachment[] = take.map((file) => ({
          kind: 'pending',
          localId: crypto.randomUUID(),
          file,
          phase: 'pending',
        }));
        return [...prev, ...additions];
      });
    },
    onError: (msg) => setDropError(msg),
  });

  // 처음 진입 시 ChatIndex 에서 넘긴 첫 메시지 자동 전송, 또는 진행 중 응답이 있으면 합류.
  // 의존성: sessionId 만. stream/tryFollow 는 매 렌더 새로 만들어지지만 의도된 1회 실행.
  // biome-ignore lint/correctness/useExhaustiveDependencies: 세션 진입 시 1회만 실행
  useEffect(() => {
    if (sentInitialRef.current === sessionId) return;
    const initial = takePendingMessage(sessionId);
    if (initial) {
      sentInitialRef.current = sessionId;
      // Index 에서 업로드한 첨부 객체를 누적 맵에 넣어 새 채팅 첫 턴에도 "(삭제됨)" 이 안 뜨게 한다.
      if (initial.files && initial.files.length > 0) mergeKnownFiles(initial.files);
      void stream(initial.content, initial.fileIds, {
        endpointId: initial.endpointId,
        bondingId: initial.bondingId,
        extendedThinking: initial.extendedThinking,
        startedAt: initial.startedAt,
      });
    } else {
      sentInitialRef.current = sessionId;
      void tryFollow();
    }
    // cleanup 을 두지 않는다 — React StrictMode 의 phantom mount→cleanup→mount 에서
    // 방금 시작한 fetch 가 cleanup 에 의해 바로 abort 되고, 두번째 mount 에서는
    // sentInitialRef 가드로 재요청을 안 해 메시지가 DB 에도 저장되지 않는 버그가 발생한다.
    // 진짜 sessionId 변경 시 abort 는 아래 별도 effect 에서 처리한다.
  }, [sessionId]);

  // isDone=true 로 마킹된 streamingArtifact 는 artifact 목록 refetch 가 완료되어
  // selected 가 non-null 이 되는 시점에 정리한다. 이렇게 하면 onArtifactCreated →
  // selected 로드 사이의 한 프레임 gap 에서도 패널이 닫히지 않는다.
  // biome-ignore lint/correctness/useExhaustiveDependencies: streamingArtifact?.isDone 변화에 반응
  useEffect(() => {
    if (sessionArtifacts.selected !== null && streamingArtifact?.isDone) {
      setStreamingArtifact(null);
    }
  }, [sessionArtifacts.selected, streamingArtifact?.isDone]);

  // sessionId 가 실제로 바뀔 때만 이전 stream 을 abort (StrictMode phantom re-run 은 skip).
  const lastSessionIdRef = useRef(sessionId);
  useEffect(() => {
    if (lastSessionIdRef.current !== sessionId) {
      abortRef.current?.abort();
      abortRef.current = null;
      lastSessionIdRef.current = sessionId;
    }
  }, [sessionId]);

  // 정지 버튼 — 서버에 cancel 알리면 서버가 LLM 을 abort 하고 부분 응답을
  // metadata.canceled=true 로 저장한 뒤 done 을 emit. 클라이언트 SSE 는 abort 하지 않음 —
  // 그 done 으로 streamMut 가 자연 resolve 되어 finally 의 invalidateQueries 까지 흐른다.
  // (즉시 abort 하면 done 을 못 받아 "중단됨" 메시지가 화면에 반영되지 않는다.)
  const onStop = useCallback(async () => {
    try {
      await api.chatCancelStream(sessionId);
    } catch {
      // 이미 종료된 stream 일 수 있음 — 무시.
    }
  }, [sessionId]);

  const onDelete = async () => {
    if (!window.confirm('이 채팅을 삭제할까요?')) return;
    await api.chatDeleteSession(sessionId);
    qc.invalidateQueries({ queryKey: ['chat', 'sessions'] });
    navigate({ to: '/chat' });
  };

  const favoriteMut = useMutation({
    mutationFn: (next: boolean) => api.chatToggleFavorite(sessionId, { isFavorite: next }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['chat', 'sessions'] });
      qc.invalidateQueries({ queryKey: ['chat', 'session', sessionId] });
    },
  });

  const compactMut = useMutation({
    mutationFn: () => api.chatCompactSession(sessionId),
    onSuccess: (result) => {
      // 헤더 chip 은 다음 turn 의 emit 으로 갱신되므로 일단 0% 처럼 보이게 (사용자 피드백).
      setContextUsage((cur) => (cur ? { ...cur, estimatedTokens: 0, usageRatio: 0 } : cur));
      // 메시지 목록 새로고침 — 옛 메시지에 archived_at 이 박혀 UI 가 회색 처리.
      qc.invalidateQueries({ queryKey: ['chat', 'session', sessionId] });
      setError(
        `대화 ${result.summarizedCount}개를 ${result.summaryChars}자로 압축했어요. 이제 LLM 은 요약 + 최근 5턴만 봅니다.`,
      );
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : '압축 실패');
    },
  });

  const onCompact = () => {
    if (compactMut.isPending) return;
    if (
      !window.confirm(
        '이전 대화를 LLM 으로 요약하고 메시지는 화면에 보존하지만 LLM 컨텍스트에서는 제외합니다. 진행할까요?',
      )
    )
      return;
    compactMut.mutate();
  };

  const messages = sessionQuery.data?.messages ?? [];
  const title = sessionQuery.data?.title ?? '';

  // 마지막 사용자 메시지 인라인 편집 → 재전송. 화면에 보이는 마지막 user 메시지(실제 입력)부터
  // 이후(구 응답/도구)를 캐시에서 낙관적으로 잘라낸 뒤, 서버에는 replaceFromMessageId 로 같은
  // 범위를 선삭제하도록 지시하고 수정본을 새 turn 으로 스트리밍한다. 첨부는 원본 그대로 유지.
  const onEditResend = (newContent: string) => {
    let target: ChatMessage | undefined;
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (!m || m.role !== 'user') continue;
      const meta = m.metadata as
        | { kind?: string; interruptKind?: string; skillId?: string }
        | null
        | undefined;
      // skill picker / router chooser 가 만든 숨김 user 메시지는 건너뛴다 (MessageList 와 동일 규칙).
      if (
        meta?.kind === 'ask_user_choice' &&
        (meta.interruptKind === 'skill_select' || typeof meta.skillId === 'string')
      ) {
        continue;
      }
      target = m;
      break;
    }
    if (!target) return;
    const targetId = target.id;
    const fileIds = target.attachedFileIds ?? [];
    qc.setQueryData<ChatSessionDetail>(['chat', 'session', sessionId], (old) => {
      if (!old) return old;
      const idx = old.messages.findIndex((m) => m.id === targetId);
      if (idx === -1) return old;
      return { ...old, messages: old.messages.slice(0, idx) };
    });
    void stream(newContent, fileIds, { replaceFromMessageId: targetId });
  };

  // sessionQuery 첫 fetch 가 서버의 user 메시지 INSERT 보다 늦게 응답하면
  // messages 의 마지막에 방금 저장된 user 가 포함되어 pendingUser 와 중복 렌더된다.
  const lastMessage = messages[messages.length - 1];
  const visiblePendingUser =
    pendingUser && (lastMessage?.role !== 'user' || lastMessage.content !== pendingUser.content)
      ? pendingUser
      : null;

  // 우선순위: 스트리밍 중인 artifact > 완성된 artifact (selected).
  // create / update 모두 스트리밍 중에는 코드뷰 + 진행 ticker 를 보여줘야 사용자가 진행 상황을
  // 본다. update 끝나면 streamingArtifact 가 null 로 클리어되며 sessionArtifacts.selected
  // (새 버전 fetch 완료) 로 자연스럽게 swap 된다.
  const showArtifactPanel =
    peekedWikiSlug !== null ||
    sessionArtifacts.selected !== null ||
    sessionArtifacts.isPendingLoad ||
    streamingArtifact !== null;

  // 세션 전환 시 우측 아티팩트 패널을 초기화하되, 공유 링크 (?artifact=<id>) 로 진입한 경우엔
  // 해당 artifact 를 자동 선택한다. 파라미터가 없으면 null → 패널 닫힘 (다른 세션으로 이동 시 잔존 방지).
  const search = chatSessionRoute.useSearch();
  const initialArtifactParam = search.artifact;
  // biome-ignore lint/correctness/useExhaustiveDependencies: selectArtifact 는 stable. sessionId / param 변화에만 반응.
  useEffect(() => {
    sessionArtifacts.selectArtifact(initialArtifactParam ?? null);
  }, [sessionId, initialArtifactParam]);

  // 리사이즈 값 persist.
  useEffect(() => {
    window.localStorage.setItem(PANEL_WIDTH_STORAGE_KEY, String(panelWidth));
  }, [panelWidth]);

  // 전체화면 ESC 해제.
  useEffect(() => {
    if (!isFullscreen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsFullscreen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isFullscreen]);

  // 모바일에서 패널 (artifact / wiki peek / streaming) 이 열린 동안 history state push.
  // 사용자가 브라우저 / Android 뒤로가기 시 popstate → 패널 닫힘. X 로 닫으면 push 한 state 정리.
  useEffect(() => {
    if (!isMobile || !showArtifactPanel) return;
    window.history.pushState({ innermatePanel: true }, '');
    const onPop = () => {
      // 뒤로가기로 도달 — 모든 패널 닫기.
      setPeekedWikiSlug(null);
      sessionArtifacts.selectArtifact(null);
      setStreamingArtifact(null);
      setIsFullscreen(false);
    };
    window.addEventListener('popstate', onPop);
    return () => {
      window.removeEventListener('popstate', onPop);
      // X 로 닫혀서 effect cleanup 이 실행된 경우 — 우리가 push 한 history 항목을 정리.
      const state = window.history.state as { innermatePanel?: boolean } | null;
      if (state?.innermatePanel) window.history.back();
    };
  }, [isMobile, showArtifactPanel, sessionArtifacts]);

  // 컴포넌트 언마운트 시 shareFeedback 타이머 정리.
  useEffect(() => {
    return () => {
      if (shareFeedbackTimerRef.current !== null) {
        window.clearTimeout(shareFeedbackTimerRef.current);
      }
    };
  }, []);

  const onSplitterPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      e.preventDefault();
      const handle = e.currentTarget;
      const startX = e.clientX;
      const startWidth = panelWidth;
      try {
        handle.setPointerCapture(e.pointerId);
      } catch {
        // setPointerCapture 가 일부 브라우저에서 실패해도 드래그 자체는 진행.
      }
      const onMove = (me: PointerEvent) => {
        // 우측 패널이므로 pointer 가 왼쪽으로 이동할수록 패널이 넓어진다.
        const delta = startX - me.clientX;
        const max = Math.max(PANEL_WIDTH_MIN + 60, window.innerWidth - 420);
        setPanelWidth(Math.max(PANEL_WIDTH_MIN, Math.min(max, startWidth + delta)));
      };
      const onUp = (ue: PointerEvent) => {
        try {
          handle.releasePointerCapture(ue.pointerId);
        } catch {
          // noop
        }
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
      };
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
    },
    [panelWidth],
  );

  const selectedArtifactId = sessionArtifacts.selected?.id ?? null;
  const onShare = useCallback(async () => {
    if (!selectedArtifactId) return;
    const url = `${window.location.origin}/chat/${sessionId}?artifact=${selectedArtifactId}`;
    try {
      await navigator.clipboard.writeText(url);
      setShareFeedback('copied');
    } catch {
      setShareFeedback('error');
    }
    if (shareFeedbackTimerRef.current !== null) {
      window.clearTimeout(shareFeedbackTimerRef.current);
    }
    shareFeedbackTimerRef.current = window.setTimeout(() => {
      setShareFeedback('idle');
      shareFeedbackTimerRef.current = null;
    }, 2000);
  }, [selectedArtifactId, sessionId]);

  const onToggleFullscreen = useCallback(() => {
    setIsFullscreen((prev) => !prev);
  }, []);

  const onCloseArtifact = useCallback(() => {
    setIsFullscreen(false);
    sessionArtifacts.selectArtifact(null);
  }, [sessionArtifacts]);

  if (sessionQuery.isError) {
    const err = sessionQuery.error;
    if (err instanceof ApiError && err.status === 404) {
      return (
        <div className="flex-1 flex items-center justify-center px-6">
          <div className="mx-auto max-w-md py-12 text-center">
            <h1 className="mb-3 text-2xl font-semibold">채팅을 찾을 수 없습니다</h1>
            <p className="text-foreground-muted">
              요청하신 채팅이 존재하지 않거나 이미 삭제되었습니다.
            </p>
          </div>
        </div>
      );
    }
    return (
      <div className="flex-1 flex items-center justify-center px-6">
        <p className="text-destructive text-sm">채팅을 불러오지 못했습니다</p>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <FileDropOverlay visible={isDragging} />
      <header className="flex items-center justify-between px-3 sm:px-6 h-14 border-b border-border">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <SidebarOpenerButton />
          {renamingTitle && session ? (
            <InlineRenameInput
              session={session}
              onDone={() => setRenamingTitle(false)}
              className="flex-1 min-w-0 bg-transparent text-sm font-medium text-foreground outline-none border-b border-accent"
            />
          ) : (
            <h1
              className="text-sm font-medium truncate cursor-text"
              title="더블클릭으로 이름 변경"
              onDoubleClick={() => {
                if (session) setRenamingTitle(true);
              }}
            >
              {title || '새 채팅'}
            </h1>
          )}
        </div>
        <div className="flex items-center gap-2">
          {contextUsage ? (
            <span
              className={`text-xs px-2 py-0.5 rounded font-mono ${
                contextUsage.usageRatio >= 0.95
                  ? 'bg-destructive/15 text-destructive'
                  : contextUsage.usageRatio >= 0.8
                    ? 'bg-amber-500/15 text-amber-500'
                    : 'bg-foreground-faint/10 text-foreground-faint'
              }`}
              title={`추정 ${contextUsage.estimatedTokens.toLocaleString()} / ${contextUsage.contextWindow.toLocaleString()} 토큰`}
            >
              {Math.round(contextUsage.usageRatio * 100)}%
            </span>
          ) : null}
          {contextUsage && contextUsage.usageRatio >= 0.8 ? (
            <button
              type="button"
              onClick={onCompact}
              disabled={compactMut.isPending}
              aria-label="대화 압축"
              title="이전 대화를 LLM 으로 요약하고 컨텍스트에서 제외합니다 (메시지는 화면에 보존)."
              className="h-7 px-2 inline-flex items-center gap-1 rounded-md text-xs text-foreground-muted hover:bg-surface-muted hover:text-foreground disabled:opacity-50"
            >
              <Archive className="size-3.5" />
              <span>압축</span>
            </button>
          ) : null}
          {session ? (
            <button
              type="button"
              onClick={() => favoriteMut.mutate(!session.isFavorite)}
              disabled={favoriteMut.isPending}
              aria-label={session.isFavorite ? '즐겨찾기 해제' : '즐겨찾기'}
              title={session.isFavorite ? '즐겨찾기 해제' : '즐겨찾기'}
              className={`size-8 grid place-items-center rounded-md hover:bg-surface-muted disabled:opacity-50 ${
                session.isFavorite
                  ? 'text-amber-500 hover:text-amber-500'
                  : 'text-foreground-muted hover:text-foreground'
              }`}
            >
              <Star className="size-4" fill={session.isFavorite ? 'currentColor' : 'none'} />
            </button>
          ) : null}
          <button
            type="button"
            onClick={onDelete}
            aria-label="채팅 삭제"
            className="size-8 grid place-items-center rounded-md text-foreground-muted hover:bg-surface-muted hover:text-foreground"
          >
            <Trash2 className="size-4" />
          </button>
        </div>
      </header>

      <div className="flex-1 flex min-h-0">
        <div className="flex-1 flex flex-col min-h-0 min-w-0 relative">
          <div className="flex-1 min-h-0 overflow-y-auto px-6 py-6">
            <div className="max-w-3xl mx-auto">
              {sessionQuery.isLoading ? (
                <div className="text-sm text-foreground-muted">불러오는 중…</div>
              ) : (
                <MessageList
                  messages={messages}
                  isStreaming={isStreaming}
                  streamSegments={(() => {
                    // messages 의 toolCall 과 streamSegments 의 toolcall chip 이 같은 id 면
                    // 둘 다 화면에 표시되어 chip 이 두 번 찍힘. messages 에 영속된 게 정답이라
                    // stream 측 chip 을 제거. (polling/invalidate 가 messages 가져온 후 cleanup
                    // 안 되는 race 의 view-level 안전망)
                    const persistedIds = new Set<string>();
                    for (const m of messages) {
                      for (const tc of m.toolCalls ?? []) persistedIds.add(tc.id);
                    }
                    return streamSegments.filter(
                      (s) => s.type !== 'toolcall' || !persistedIds.has(s.call.id),
                    );
                  })()}
                  pendingUser={visiblePendingUser}
                  onArtifactClick={(id, version) => {
                    setPeekedWikiSlug(null);
                    sessionArtifacts.selectArtifact(id);
                    sessionArtifacts.selectVersion(version);
                  }}
                  onWikilinkPeek={(slug) => {
                    // 위키 미리보기가 우측 패널을 잠시 점유 — 닫으면 기존 artifact 가 다시 보임.
                    setPeekedWikiSlug(slug);
                  }}
                  slugToTitle={wikiSlugToTitle}
                  filesById={filesById}
                  showResponseTime={showResponseTime}
                  liveElapsedMs={streamStartedAt !== null ? liveElapsedMs : null}
                  onEditResend={onEditResend}
                />
              )}
              {error ? (
                <div className="mt-4 text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md px-3 py-2">
                  {error}
                </div>
              ) : null}
              {dropError ? (
                <div className="mt-4 text-sm text-amber-600 bg-amber-500/10 border border-amber-500/20 rounded-md px-3 py-2">
                  {dropError}
                </div>
              ) : null}
            </div>
          </div>

          <div className="shrink-0 px-6 pb-6">
            <div className="w-full max-w-3xl mx-auto mb-2">
              <ActiveSkillChips sessionId={sessionId} pending={isStreaming} />
            </div>
            <ChatComposer
              onSend={onSend}
              pending={isStreaming}
              autoFocus
              attachments={attachments}
              onAttachmentsChange={setAttachments}
              sessionId={sessionId}
              liveById={filesById}
              onStop={onStop}
              modelPicker={
                appSettings.data?.userModelSelectionEnabled === false
                  ? undefined
                  : {
                      models: model.models,
                      selected: model.selected,
                      extendedThinking: model.extendedThinking,
                      onSelect: model.setSelected,
                      onToggleThinking: model.setExtendedThinking,
                    }
              }
            />
            <ChatFooterNote />
          </div>
          {pendingHil ? (
            <HilModal
              interruptId={pendingHil.interruptId}
              prompt={pendingHil.prompt}
              onResolve={(body) => void resolveHil(body)}
              onClose={closeHil}
              pending={hilSubmitting}
            />
          ) : null}
        </div>

        {showArtifactPanel && !renderFullscreen ? (
          <>
            <div
              role="separator"
              aria-orientation="vertical"
              aria-label="패널 크기 조절"
              tabIndex={0}
              onPointerDown={onSplitterPointerDown}
              className="hidden lg:block w-1 shrink-0 cursor-col-resize bg-border hover:bg-accent active:bg-accent transition-colors"
            />
            <aside
              className="hidden lg:flex shrink-0 flex-col min-h-0 border-l border-border"
              style={{ width: panelWidth }}
            >
              {peekedWikiSlug ? (
                <WikiPeekPanel
                  slug={peekedWikiSlug}
                  onClose={() => setPeekedWikiSlug(null)}
                  slugToTitle={wikiSlugToTitle}
                  fromChatSessionId={sessionId}
                />
              ) : streamingArtifact && !streamingArtifact.isDone ? (
                // 아직 스트리밍 중 — 실시간 코드뷰
                <StreamingArtifactPanel
                  streaming={streamingArtifact}
                  onCancel={() => setStreamingArtifact(null)}
                />
              ) : sessionArtifacts.selected ? (
                // 스트림 완료 + artifact 로드됨 — 정식 뷰어
                <ArtifactPanel
                  artifact={sessionArtifacts.selected}
                  versionContent={sessionArtifacts.versionContent}
                  effectiveVersion={sessionArtifacts.effectiveVersion}
                  isLoading={sessionArtifacts.isContentLoading}
                  onClose={onCloseArtifact}
                  onSelectVersion={sessionArtifacts.selectVersion}
                  isFullscreen={false}
                  onToggleFullscreen={onToggleFullscreen}
                  onShare={onShare}
                  shareFeedback={shareFeedback}
                  onSaveDashboard={(artifactId, defaultTitle) =>
                    setSaveDashboardState({ open: true, artifactId, defaultTitle })
                  }
                  onAnalyzeDashboard={(artifactId, content) =>
                    setAnalyzeArtifactState({ open: true, artifactId, content })
                  }
                />
              ) : streamingArtifact?.isDone ? (
                // artifact 로드 대기 중이거나 생성 실패 — 스트리밍된 내용을 패널에 유지
                <StreamingArtifactPanel
                  streaming={streamingArtifact}
                  onCancel={() => setStreamingArtifact(null)}
                />
              ) : sessionArtifacts.isPendingLoad ? (
                <div className="flex h-full items-center justify-center text-sm text-foreground-muted">
                  <Loader2 className="mr-2 size-4 animate-spin" />
                  불러오는 중…
                </div>
              ) : null}
            </aside>
          </>
        ) : null}
      </div>
      {/* Fullscreen 모드 (데스크탑 사용자가 직접 토글했거나, 모바일에선 항상). 패널 종류별로 분기. */}
      {showArtifactPanel && renderFullscreen ? (
        <aside className="fixed inset-0 z-50 flex flex-col bg-surface">
          {peekedWikiSlug ? (
            <WikiPeekPanel
              slug={peekedWikiSlug}
              onClose={() => setPeekedWikiSlug(null)}
              slugToTitle={wikiSlugToTitle}
            />
          ) : streamingArtifact && !streamingArtifact.isDone ? (
            <StreamingArtifactPanel
              streaming={streamingArtifact}
              onCancel={() => setStreamingArtifact(null)}
            />
          ) : sessionArtifacts.selected ? (
            <ArtifactPanel
              artifact={sessionArtifacts.selected}
              versionContent={sessionArtifacts.versionContent}
              effectiveVersion={sessionArtifacts.effectiveVersion}
              isLoading={sessionArtifacts.isContentLoading}
              onClose={onCloseArtifact}
              onSelectVersion={sessionArtifacts.selectVersion}
              isFullscreen
              onToggleFullscreen={onToggleFullscreen}
              onShare={onShare}
              shareFeedback={shareFeedback}
              onSaveDashboard={(artifactId, defaultTitle) =>
                setSaveDashboardState({ open: true, artifactId, defaultTitle })
              }
              onAnalyzeDashboard={(artifactId, content) =>
                setAnalyzeArtifactState({ open: true, artifactId, content })
              }
            />
          ) : streamingArtifact?.isDone ? (
            <StreamingArtifactPanel
              streaming={streamingArtifact}
              onCancel={() => setStreamingArtifact(null)}
            />
          ) : sessionArtifacts.isPendingLoad ? (
            <div className="flex h-full items-center justify-center text-sm text-foreground-muted">
              <Loader2 className="mr-2 size-4 animate-spin" />
              불러오는 중…
            </div>
          ) : null}
        </aside>
      ) : null}
      {analyzeArtifactState.open ? (
        <ArtifactProfilePanel
          artifactId={analyzeArtifactState.artifactId}
          content={analyzeArtifactState.content}
          onClose={() => setAnalyzeArtifactState({ open: false })}
        />
      ) : null}
      <DashboardSaveDialog
        open={saveDashboardState.open}
        onOpenChange={(o) =>
          setSaveDashboardState(
            o ? (saveDashboardState.open ? saveDashboardState : { open: false }) : { open: false },
          )
        }
        artifactId={saveDashboardState.open ? saveDashboardState.artifactId : null}
        defaultTitle={saveDashboardState.open ? saveDashboardState.defaultTitle : ''}
        onSaved={() => {
          // 저장 직후 좌측 사이드바를 자동 펼침 — 새로 추가된 대시보드 행을 즉시 보이게.
          openSidebar();
        }}
      />
    </div>
  );
}
